import React from "react";
import { MoreVertical, Pencil, Power } from "lucide-react";

import TableV2 from "src/components/ui/TableV2";
import Popover from "src/components/ui/Popover";
import { Badge } from "src/components/ui/Badge";
import { formatDateTime } from "src/utils/date-time";
import { formatCreatedBy } from "src/utils/common";

interface TenantUserListTableProps {
  data: any[];
  pagination?: any;
  onPageChange: (page: number) => void;
  onEdit?: (row: any) => void;
  onToggleStatus?: (row: any) => void;
  canEdit?: boolean;
  canToggle?: boolean;
  loading?: boolean;
}

const TenantUserListTable: React.FC<TenantUserListTableProps> = ({
  data,
  pagination,
  onPageChange,
  onEdit,
  onToggleStatus,
  canEdit = false,
  canToggle = false,
  loading = false,
}) => {
  return (
    <TableV2
      columns={[
        {
          key: "name",
          header: "Name",
          accessor: ({ row }) => (
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {[row["firstName"], row["lastName"]].filter(Boolean).join(" ")}
            </span>
          ),
        },
        {
          key: "email",
          header: "Email",
          accessor: ({ row }) => (
            <span className="text-gray-600 dark:text-gray-300">
              {row?.email || "-"}
            </span>
          ),
        },
        {
          key: "phone",
          header: "Phone",
          accessor: ({ row }) => (
            <span className="text-gray-600 dark:text-gray-300">
              {row?.phone || "-"}
            </span>
          ),
        },
        {
          key: "role",
          header: "Designation",
          accessor: ({ row }) => (
            <span className="text-gray-600 dark:text-gray-300">
              {row?.role?.name || row?.role || "-"}
            </span>
          ),
        },
        {
          key: "departmentId",
          header: "Department",
          accessor: ({ row }) => (
            <span className="text-gray-600 dark:text-gray-300">
              {row?.departmentId?.name || "-"}
            </span>
          ),
        },
        {
          key: "createdBy",
          header: "Created By",
          accessor: ({ row }) => formatCreatedBy(row?.createdBy),
        },
        {
          key: "createdAt",
          header: "Created At",
          accessor: ({ key, row }) => (
            <span className="text-gray-600 dark:text-gray-300">
              {formatDateTime(row[key], "DD/MM/YYYY")}
            </span>
          ),
        },
        {
          key: "status",
          header: "Status",
          accessor: ({ key, row }) => (
            <Badge
              text={row?.[key] === "active" ? "Active" : "Suspended"}
              color={row?.[key] === "active" ? "green" : "red"}
            />
          ),
        },
        {
          key: "actions",
          header: "Actions",
          accessor: ({ row }) =>
            !canEdit && !canToggle ? null : (
              <Popover
                trigger={
                  <button
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                    title="Actions"
                  >
                    <MoreVertical size={16} />
                  </button>
                }
              >
                {(close) => (
                  <div className="w-44 py-1.5">
                    {canEdit && (
                      <button
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                        onClick={() => {
                          close();
                          onEdit?.(row);
                        }}
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                    )}
                    {canToggle && (
                      <button
                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm ${
                          row.status === "active"
                            ? "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                            : "text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/30"
                        }`}
                        onClick={() => {
                          close();
                          onToggleStatus?.(row);
                        }}
                      >
                        <Power size={14} />
                        {row.status === "active" ? "Suspend" : "Activate"}
                      </button>
                    )}
                  </div>
                )}
              </Popover>
            ),
        },
      ]}
      data={data}
      pagination={pagination}
      onPageChange={onPageChange}
      loading={loading}
    />
  );
};

export default TenantUserListTable;
